import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import {
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { debug } from "../../utils";
import { createAction } from "@reduxjs/toolkit";
import { ACTION_SIDEBAR_VIEW_CHANGED } from "../LayoutReducer";

// Import Refactored Link Components
import { DashboardLink } from "./DashboardLink";
import { DevicesLink } from "./DevicesLink";
import { ValidateLink } from "./ValidateLink";
import { AnalysisLink } from "./AnalysisLink";
import { RecentViolationsLink } from "./RecentViolationsLink";
import { RemediationReportLink } from "./RemediationReportLink";
import { ConfigChangesLink } from "./ConfigChangesLink";
import { ReportsLink } from "./ReportsLink";
import { MlDetectionLink } from "./MlDetectionLink";
// import { NmapScanLink } from "./NmapScanLink";
import { MSBbLink } from "./MSBbLink";
import { SettingsLink } from "./SettingsLink";
//import { NmapLink } from "./NmapLink";
// const defaultLinkParams = {
//     devices: { 's_hostName': 'asc' }
// }

const SIDEBAR_SCROLL_KEY = "octopus-sidebar-scroll-top";

function Sidebar({ sidebarCollapsed, pageTitle, userCapability, dispatch }) {
    const location = useLocation();
    const sidebarNavRef = useRef(null);
    const [capability, setCapability] = useState(-1);

    useEffect(() => {
        if (userCapability) {
            const permission = userCapability.find(item => item.component === "systemsettings")?.permission;
            setCapability(permission);
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [userCapability]);

    useEffect(() => {
        if (sidebarCollapsed) {
            document.body.classList.add("sidebar-collapse");
        } else {
            document.body.classList.remove("sidebar-collapse");
        }
    }, [sidebarCollapsed]);

    const toggleSidebar = () => {
        dispatch(createAction(ACTION_SIDEBAR_VIEW_CHANGED)());
    };

    const currentPath = location.pathname;

    const saveSidebarScroll = useCallback(() => {
        if (sidebarNavRef.current) {
            sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(sidebarNavRef.current.scrollTop));
        }
    }, []);

    useLayoutEffect(() => {
        const savedScrollTop = Number(sessionStorage.getItem(SIDEBAR_SCROLL_KEY));

        if (!Number.isNaN(savedScrollTop) && sidebarNavRef.current) {
            sidebarNavRef.current.scrollTop = savedScrollTop;
        }
    }, [currentPath, capability, sidebarCollapsed]);

    return (
        <aside className={`sidebar-v2 ${sidebarCollapsed ? 'collapsed' : ''}`}>
            {/* Header */}
            <div className="sidebar-header">
                <div className="header-content">
                    <div className="logo-container-collapsed">
                        <span>Octopus<br />VAR</span>
                    </div>
                    <div className="logo-container-expanded">
                        <h1 className="logo-title">
                            Octopus VAR
                        </h1>
                    </div>
                </div>
            </div>

            {/* Menu Items */}
            <nav
                ref={sidebarNavRef}
                className="sidebar-nav"
                onClickCapture={saveSidebarScroll}
                onScroll={saveSidebarScroll}
            >
                <ul className="nav-list">
                    <DashboardLink isCollapsed={sidebarCollapsed} />
                    <DevicesLink isCollapsed={sidebarCollapsed} />
                    <ValidateLink isCollapsed={sidebarCollapsed} />
                    <AnalysisLink isCollapsed={sidebarCollapsed} />
                    <RecentViolationsLink isCollapsed={sidebarCollapsed} />
                    <RemediationReportLink isCollapsed={sidebarCollapsed} />
                    <ConfigChangesLink isCollapsed={sidebarCollapsed} />
                    <ReportsLink isCollapsed={sidebarCollapsed} />
                    <MlDetectionLink isCollapsed={sidebarCollapsed} />

                    {/* <NmapScanLink
                        isCollapsed={sidebarCollapsed}
                        currentPath={currentPath}
                    /> */}

                    <MSBbLink isCollapsed={sidebarCollapsed} />

                    {/* Only show settings if user has capability */}
                    {capability > -1 && (
                        <SettingsLink
                            isCollapsed={sidebarCollapsed}
                            currentPath={currentPath}
                            userCapability={userCapability}
                        />
                    )}
                </ul>
            </nav>

            {/* Footer - Collapse Toggle */}
            <div className="sidebar-footer">
                <button
                    onClick={toggleSidebar}
                    className={`collapse-btn ${sidebarCollapsed ? 'collapsed-mode' : ''}`}
                    title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                >
                    <div className="collapse-icon">
                        {sidebarCollapsed ? (
                            <ChevronRight className="w-5 h-5" />
                        ) : (
                            <ChevronLeft className="w-5 h-5" />
                        )}
                    </div>
                    <span className="collapse-label">Collapse</span>
                </button>
            </div>
        </aside>
    );
}

function mapStateToProps(state) {
    debug("Sidebar mapStateToProps", state);
    const auth = state.auth;
    return {
        userCapability: auth.userCapability,
        sidebarCollapsed: state.layout.sidebarCollapsed,
        pageTitle: state.layout.pageTitle
    };
}

export default connect(mapStateToProps)(Sidebar);
